import NotFoundError from "pages/404";

import Layout from "components/layout";
import SubjectGroup from "components/pages/study_plan/subject_group";
import Steps from "components/pages/study_plan/steps";
import Link from "next/link";

import * as studyPlansApi from "api/study_plans";
import { studyPlan as studyPlanFixture } from "components/pages/study_plan/fixtures";
import { track } from "utils/tracker";
import { resetScroll } from "utils/browser";

import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useMounted } from "hooks/react";
import { useProfile } from "hooks/profile";

export default function StudyPlan(): JSX.Element {
  const [studyPlan, setStudyPlan] = useState(studyPlanFixture);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const {
    query: { etapa: stepParam },
    isReady
  } = useRouter();
  const router = useRouter();
  const step = Number(stepParam) || 1;

  const { currentStudyPlanStep, isLoading: isFetchingCurrentStep } =
    useProfile();
  const currentStep = currentStudyPlanStep || 1;

  useMounted(handleMount);

  useEffect(() => {
    if (!isReady || isFetchingCurrentStep) return;

    function fetchStudyPlan() {
      setLoading(true);
      studyPlansApi
        .index(step)
        .then((response) => {
          setStudyPlan(response.body.data);
          setTotalPages(response.body.totalPages);
          setLoading(false);
        })
        .catch((error) => {
          if (error.statusCode !== 404) throw error;

          setLoading(false);
          setStudyPlan(null);
        });
    }

    if (stepParam || currentStep === 1) return fetchStudyPlan();

    router.push(`/plano?etapa=${currentStep}`);
  }, [step, isReady, isFetchingCurrentStep]);

  function handleMount() {
    resetScroll();
    track("Visited study plan");
  }

  function groupsFromPlan() {
    const groups = [];
    studyPlan.topics.forEach((topic) => {
      if (groups.find((group) => group.slug === topic.group.slug)) return;

      groups.push(topic.group);
    });
    return groups;
  }

  function topicsFromGroup(group) {
    return studyPlan.topics.filter((topic) => {
      return topic.group.slug === group.slug;
    });
  }

  function handlePagination(direction) {
    const newStep = direction === "prev" ? step - 1 : step + 1;
    router.push(`/plano?etapa=${newStep}`);
  }

  if (!loading && !studyPlan) return <NotFoundError />;

  return (
    <Layout
      title="Plano de estudos"
      description="Plano de estudo gratuito para o ENEM e vestibulares, com exercícios, simulados e videoaulas, categorizados e agrupados por matéria e assunto."
      canonical="/plano"
    >
      <main>
        <div className="container">
          <Link href="/plano/editar">
            <a
              className="btn btn-secondary mb-6 w-full"
              onClick={() => track("Clicked on study plan edit link")}
            >
              Personalizar plano
            </a>
          </Link>

          <Steps
            step={step}
            onPaginate={handlePagination}
            totalPages={totalPages}
            loading={loading}
            completedRate={studyPlan.completedRate}
          />

          <div className="topics mt-8">
            {groupsFromPlan().map((group) => {
              return (
                <div key={group.slug} className="group mb-12">
                  <SubjectGroup
                    step={step}
                    group={group}
                    topics={topicsFromGroup(group)}
                    loading={loading}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </main>
    </Layout>
  );
}
